import { useCallback, useEffect, useState } from 'react';
import { getPreviousHomologousPeriod, getSalesDateRange } from '../../sales/public-api'
import { getSalesSnapshotByDateRange } from '../../sales/public-api';
import { readFirstSaleCreatedAtIso } from './day1PathEvidence';

export type WeeklyHomeSummary = {
  weekTotal: number;
  weekSalesCount: number;
  prevWeekTotal: number;
  pctVsPrev: number | null;
  daysSinceFirstSale: number;
};

const MIN_DAYS_SINCE_FIRST_SALE = 7;

export function useWeeklyHomeSummary(branchId: string | undefined) {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<WeeklyHomeSummary | null>(null);

  const reload = useCallback(async () => {
    if (!branchId) {
      setSummary(null);
      return;
    }
    setLoading(true);
    try {
      const firstIso = await readFirstSaleCreatedAtIso(branchId);
      if (!firstIso) {
        setSummary(null);
        return;
      }
      const firstMs = Date.parse(firstIso);
      const daysSinceFirstSale = Math.floor((Date.now() - firstMs) / (24 * 60 * 60 * 1000));
      if (daysSinceFirstSale < MIN_DAYS_SINCE_FIRST_SALE) {
        setSummary(null);
        return;
      }

      const now = new Date();
      const weekRange = getSalesDateRange(now, 'week');
      const prevRange = getPreviousHomologousPeriod(now, 'week', weekRange);
      const [weekSnap, prevSnap] = await Promise.all([
        getSalesSnapshotByDateRange(branchId, weekRange.fromIso, weekRange.toIso),
        prevRange
          ? getSalesSnapshotByDateRange(branchId, prevRange.fromIso, prevRange.toIso)
          : Promise.resolve({ salesTotal: 0, salesCount: 0 }),
      ]);

      const weekTotal = weekSnap.salesTotal;
      const prevWeekTotal = prevSnap.salesTotal;
      const pctVsPrev =
        prevWeekTotal > 0 ? Math.round(((weekTotal - prevWeekTotal) / prevWeekTotal) * 100) : null;

      setSummary({
        weekTotal,
        weekSalesCount: weekSnap.salesCount,
        prevWeekTotal,
        pctVsPrev,
        daysSinceFirstSale,
      });
    } catch {
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { loading, summary, reload, minDaysSinceFirstSale: MIN_DAYS_SINCE_FIRST_SALE };
}
